import { apiClient } from './apiClient';
import { authService } from './authService';
import type {
  CreateReservationRequest,
  ReservationResponse,
  GetReservationsParams,
  CancelReservationRequest,
  CancelSlotsRequest,
  CancelSlotsResponse,
  ReservationWithSlotsResponse,
  ApplyProductRequest,
  ApplyProductResponse,
  RescheduleReservationRequest,
  RescheduleReservationResponse,
  GroupReservationRequest,
  GroupReservationResponse,
} from '../types/reservation';

export const reservationService = {
  /**
   * Create a new reservation
   */
  async createReservation(
    data: CreateReservationRequest
  ): Promise<ReservationResponse> {
    const token = authService.storage.getToken();
    return apiClient.post<ReservationResponse>(
      '/Reservation/admin',
      data,
      token || undefined
    );
  },

  /**
   * Get reservations with optional filters
   */
  async getReservations(
    params?: GetReservationsParams
  ): Promise<ReservationResponse[]> {
    const token = authService.storage.getToken();
    const queryParams = new URLSearchParams();

    if (params?.facilityId) queryParams.append('facilityId', params.facilityId);
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const query = queryParams.toString();
    const endpoint = query ? `/Reservation?${query}` : '/Reservation';

    return apiClient.get<ReservationResponse[]>(endpoint, token || undefined);
  },

  /**
   * Get a single reservation by ID
   */
  async getReservationById(id: string): Promise<ReservationResponse> {
    const token = authService.storage.getToken();
    return apiClient.get<ReservationResponse>(
      `/Reservation/${id}`,
      token || undefined
    );
  },

  /**
   * Update a reservation
   */
  async updateReservation(
    id: string,
    data: Partial<CreateReservationRequest>
  ): Promise<ReservationResponse> {
    const token = authService.storage.getToken();
    return apiClient.put<ReservationResponse>(
      `/Reservation/${id}`,
      data,
      token || undefined
    );
  },

  /**
   * Delete a reservation
   */
  async deleteReservation(id: string): Promise<void> {
    const token = authService.storage.getToken();
    return apiClient.delete<void>(`/Reservation/${id}`, token || undefined);
  },

  /**
   * Cancel entire reservation by agent
   */
  async cancelReservationByAgent(
    id: string,
    data?: CancelReservationRequest
  ): Promise<ReservationResponse> {
    const token = authService.storage.getToken();
    return apiClient.post<ReservationResponse>(
      `/reservation/${id}/agent-cancel`,
      { notes: data?.notes || '' },
      token || undefined
    );
  },

  /**
   * Cancel selected slots from a reservation
   */
  async cancelSlots(
    id: string,
    data: CancelSlotsRequest
  ): Promise<CancelSlotsResponse> {
    const token = authService.storage.getToken();
    return apiClient.post<CancelSlotsResponse>(
      `/reservation/${id}/cancel-slots`,
      data,
      token || undefined
    );
  },

  /**
   * Get reservation details with slots
   */
  async getReservationSlots(id: string): Promise<ReservationWithSlotsResponse> {
    const token = authService.storage.getToken();
    return apiClient.get<ReservationWithSlotsResponse>(
      `/reservation/${id}/slots`,
      token || undefined
    );
  },

  /**
   * Toggle payment status (paid ↔ unpaid) - marks as paid with cash
   */
  async toggleReservationPayment(id: string): Promise<ReservationResponse> {
    const token = authService.storage.getToken();
    return apiClient.patch<ReservationResponse>(
      `/reservation/${id}/toggle-payment`,
      {},
      token || undefined
    );
  },

  /**
   * Apply a product purchase to a reservation
   * @param id - Reservation ID
   * @param data - Purchase ID prefix and user email
   */
  async applyProduct(
    id: string,
    data: ApplyProductRequest
  ): Promise<ApplyProductResponse> {
    const token = authService.storage.getToken();
    return apiClient.post<ApplyProductResponse>(
      `/reservation/${id}/apply-product`,
      data,
      token || undefined
    );
  },

  /**
   * Reschedule a reservation to a new date/time
   * @param id - Reservation ID
   * @param data - New date, time slots, optional facility change, and notes
   */
  async rescheduleReservation(
    id: string,
    data: RescheduleReservationRequest
  ): Promise<RescheduleReservationResponse> {
    const token = authService.storage.getToken();
    return apiClient.post<RescheduleReservationResponse>(
      `/reservation/${id}/reschedule`,
      data,
      token || undefined
    );
  },

  /**
   * Create group reservation across multiple facilities (admin endpoint)
   * @param data - Group reservation data with facility reservations array
   */
  async createGroupReservation(
    data: GroupReservationRequest
  ): Promise<GroupReservationResponse> {
    const token = authService.storage.getToken();
    return apiClient.post<GroupReservationResponse>(
      '/reservation/admin/group',
      data,
      token || undefined
    );
  },

  /**
   * Update reservation notes
   * @param id - Reservation ID
   * @param notes - New notes content
   */
  async updateReservationNotes(
    id: string,
    notes: string
  ): Promise<ReservationResponse> {
    const token = authService.storage.getToken();
    return apiClient.patch<ReservationResponse>(
      `/reservation/${id}/notes`,
      { notes },
      token || undefined
    );
  },
};
